import { useEffect, useMemo, useRef, useState } from "react";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { findEmoji, loadEmoji } from "./emoji";
import { captureElementToBlob, downloadBlob } from "./lib/export";
import { EmojiPicker } from "./components/EmojiPicker";
import { Editor, type Canvas } from "./editor/Editor";
import { Toolbar } from "./editor/Toolbar";
import { insertEmoji } from "./editor/setup";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UILabel } from "@/components/ui/label";

const INITIAL_CANVAS: Canvas = { width: 991, height: 306, padding: 24 };

export function LabelView() {
  const [canvas, setCanvas] = useState<Canvas>(INITIAL_CANVAS);
  const [state, setState] = useState<EditorState | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [emojiReady, setEmojiReady] = useState(false);
  const viewRef = useRef<EditorView | null>(null);

  useEffect(() => {
    loadEmoji()
      .then(() => setEmojiReady(true))
      .catch((e) => setStatus("failed to load emoji: " + e.message));
  }, []);

  const filename = useMemo(() => {
    const first = state?.doc.firstChild?.textContent ?? "label";
    const slug =
      first
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40) || "label";
    return `${slug}.png`;
  }, [state]);

  const onExport = async () => {
    const view = viewRef.current;
    if (!view) return;
    setStatus("capturing…");
    const blob = await captureElementToBlob(view.dom as HTMLElement);
    downloadBlob(blob, filename);
    setStatus(`saved ${filename}`);
  };

  const onPickEmoji = (slug: string) => {
    const entry = findEmoji(slug);
    const view = viewRef.current;
    if (!entry || !view) return;
    insertEmoji(entry.slug, entry.hex)(view.state, view.dispatch);
    view.focus();
    setPickerOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="border-b px-6 py-3 flex items-center justify-end gap-3">
        <span className="text-xs text-muted-foreground flex-1">{status}</span>
        <Button onClick={onExport} disabled={!state}>
          Download PNG
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-[380px_1fr] gap-0 min-h-0">
        <aside className="border-r p-4 overflow-y-auto space-y-4">
          <section className="grid grid-cols-3 gap-2">
            <div>
              <UILabel className="text-xs">Width</UILabel>
              <Input
                type="number"
                value={canvas.width}
                onChange={(e) => setCanvas({ ...canvas, width: +e.target.value })}
              />
            </div>
            <div>
              <UILabel className="text-xs">Height</UILabel>
              <Input
                type="number"
                value={canvas.height}
                onChange={(e) => setCanvas({ ...canvas, height: +e.target.value })}
              />
            </div>
            <div>
              <UILabel className="text-xs">Padding</UILabel>
              <Input
                type="number"
                value={canvas.padding}
                onChange={(e) => setCanvas({ ...canvas, padding: +e.target.value })}
              />
            </div>
          </section>

          <hr />

          <Toolbar state={state} view={viewRef.current} onOpenPicker={() => setPickerOpen(true)} />
        </aside>

        <main className="flex items-center justify-center overflow-auto bg-muted p-8">
          <div className="shadow-lg">
            {emojiReady && (
              <Editor
                canvas={canvas}
                onReady={(v) => (viewRef.current = v)}
                onState={setState}
              />
            )}
          </div>
        </main>
      </div>

      {pickerOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50"
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="bg-background border rounded-t-lg md:rounded-lg w-full md:max-w-2xl h-[70vh] p-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium">Insert emoji</h2>
              <Button variant="ghost" size="sm" onClick={() => setPickerOpen(false)}>
                Close
              </Button>
            </div>
            <EmojiPicker onPick={onPickEmoji} />
          </div>
        </div>
      )}
    </div>
  );
}
